import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/material'
import PropTypes from 'prop-types'
import useSWR from 'swr'
import { actions } from '~/api'
import {
  Dialog,
  Dropzone,
  Link,
  MultilineEllipsis,
  Table,
  Title,
} from '~/components'
import { useCanEdit, useDateTimeFormatter } from '~/hooks'
import { useRouteContext } from '~/routes'
import { isSameDay } from '~/utils'

const acceptedTypes = {
  'text/xml': ['.xml'],
  'application/zip': ['.zip'],
}

const errorClassName = 'text-danger italic'
const Error = (props) => {
  const error = props.error?.replace(/\n/g, '<br />')

  const [expandedError, setExpandedError] = useState(null)
  const expanded = expandedError === error

  return (
    <div
      className="cursor-pointer"
      onClick={() =>
        setExpandedError((current) => (current === error ? null : error))
      }
    >
      {expanded ? (
        <div
          className={errorClassName}
          dangerouslySetInnerHTML={{ __html: error }}
        />
      ) : (
        <MultilineEllipsis className={errorClassName} text={error} />
      )}
    </div>
  )
}

Error.propTypes = {
  error: PropTypes.string,
}

const Import = () => {
  const { t } = useTranslation()
  const canEdit = useCanEdit()
  const { brand } = useRouteContext(Import.name)
  const dateTimeFormat = useDateTimeFormatter()

  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false)
  const [files, setFiles] = useState()

  const { data, mutate } = useSWR(
    brand.url ? `logs/${brand.url}` : null,
    () => actions.import.getLogs(brand.url),
    {
      refreshInterval: (latest) => (latest?.processing?.length ? 10000 : 0),
    }
  )

  const onDrop = useCallback((acceptedFiles) => {
    if (!Array.isArray(acceptedFiles)) return
    setFiles(acceptedFiles)
    setShowOverwriteDialog(true)
  }, [])

  const uploadFiles = useCallback(
    async (overwrite) => {
      const result = await actions.import.uploadFiles(
        brand.url,
        files,
        overwrite
      )
      await mutate(result, { revalidate: false })
      setFiles()
    },
    [brand.url, files, mutate]
  )

  const hasProgress = files?.length > 0 || data?.processing?.length > 0

  const info = hasProgress ? (
    <>
      {t('import.progress')}:
      <br />
      {data?.processing?.length <= 0 &&
        files.map((file, index) => (
          <div key={index} className="text-xs">
            {file.name}
          </div>
        ))}
      {data?.processing?.map?.((file, index) => (
        <div key={index} className="text-xs">
          {file.saved ? file.saved : '0'} z{' '}
          {file.products ? file.products : '?'} - {file.name}
        </div>
      ))}
    </>
  ) : undefined

  const logs =
    data?.logs?.length > 0 ? (
      <Table
        hover
        pagination={false}
        rows={data.logs.map((row, index) => {
          const prev = data.logs[index - 1]?.created_at
          const content =
            row.product && !row.error ? (
              <span className={row.product.trashed ? 'trashed' : ''}>
                <span>{row.product.path} </span>
                <Link to={`/${brand.url}/specs/product/${row.product.id}`}>
                  {row.product.name}
                </Link>
                <span> - {row.status}</span>
              </span>
            ) : (
              <Error error={row.error} />
            )
          const createdAt = (
            <span className="whitespace-nowrap">
              {dateTimeFormat(row.created_at, {
                hideDate: isSameDay(row.created_at, prev),
              })}
            </span>
          )
          const cells = [
            {
              content,
              sx: { padding: 0 },
            },
            {
              content: createdAt,
              sx: {
                padding: 0,
                textAlign: 'right',
                verticalAlign: 'top',
              },
            },
          ]
          return { cells }
        })}
      />
    ) : undefined

  return (
    <Stack spacing={1.5}>
      <Title variant="h2" component="h2">
        {t('import.title')}
      </Title>
      {canEdit && (
        <>
          <Dropzone accept={acceptedTypes} onDrop={onDrop}>
            {info}
          </Dropzone>
          <Dialog
            actions={[
              {
                id: 'overwrite',
                name: t('import.overwrite.overwrite'),
                variant: 'contained',
              },
              {
                id: 'skip',
                name: t('import.overwrite.skip'),
                variant: 'outlined',
                autoFocus: true,
              },
            ]}
            body={t('import.overwrite.message')}
            fullWidth
            maxWidth="xs"
            open={showOverwriteDialog}
            persistant
            title={t('import.overwrite.title')}
            onClose={(action) => {
              setShowOverwriteDialog(false)
              uploadFiles(action === 'overwrite')
            }}
          />
        </>
      )}
      {logs}
    </Stack>
  )
}

export default Import
